// src/worker/achievements.js — 🏆 the Achievements core (v126, Part 1).
//
// PURE functions only: period math, the badge catalogue, winner-picking and
// the per-badge metric computes. NO fetches, NO sb() — the cron/route layer
// (achievements-run.js, PR2) loads rows and calls these, the same split as
// the other pure-compute modules. Everything here is unit-tested with fixtures.
//
// Fairness rules (spec, signed off 2026-07-18):
//   - metrics are %/avg/vs-size ratios, never raw totals (store size can't win)
//   - minimum-activity floors stop empty-data wins
//   - ties → joint winners, EXCEPT customer-favourites (tie → review COUNT,
//     Matt's rule)
//   - celebrate, never shame: computes return WINNERS (+podium detail);
//     nothing here ever emits a "worst" list.

// ── Period math ───────────────────────────────────────────────────────────────
// All period keys are derived from BUSINESS-DAY date strings ('YYYY-MM-DD',
// already 3am-Dubai-resolved by the caller). Internally UTC so DST/locale can
// never shift a boundary.

function d(dateStr) {
  const [y, m, dd] = String(dateStr).split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, dd));
}
function ymdUTC(dt) {
  return dt.toISOString().slice(0, 10);
}
function addDays(dateStr, n) {
  const dt = d(dateStr); dt.setUTCDate(dt.getUTCDate() + n); return ymdUTC(dt);
}

// ISO week: Monday-start, week 1 contains the first Thursday.
export function weekKey(dateStr) {
  const dt = d(dateStr);
  const day = (dt.getUTCDay() + 6) % 7;              // Mon=0..Sun=6
  dt.setUTCDate(dt.getUTCDate() - day + 3);          // the week's Thursday
  const year = dt.getUTCFullYear();
  const jan4 = new Date(Date.UTC(year, 0, 4));
  const week = 1 + Math.round(((dt - jan4) / 86400000 - 3 + ((jan4.getUTCDay() + 6) % 7)) / 7);
  return year + '-W' + String(week).padStart(2, '0');
}
export function monthKey(dateStr)   { return String(dateStr).slice(0, 7); }
export function quarterKey(dateStr) {
  const m = Number(String(dateStr).slice(5, 7));
  return String(dateStr).slice(0, 4) + '-Q' + (Math.floor((m - 1) / 3) + 1);
}
export function yearKey(dateStr)    { return String(dateStr).slice(0, 4); }

// periodRange(key) -> { from, toExcl } (business-day date strings, toExcl exclusive)
export function periodRange(key) {
  const k = String(key);
  let m;
  if ((m = /^(\d{4})-W(\d{2})$/.exec(k))) {
    // Monday of ISO week w: Jan 4 is always in week 1.
    const year = Number(m[1]), week = Number(m[2]);
    const jan4 = new Date(Date.UTC(year, 0, 4));
    const mon1 = new Date(jan4); mon1.setUTCDate(jan4.getUTCDate() - ((jan4.getUTCDay() + 6) % 7));
    const from = new Date(mon1); from.setUTCDate(mon1.getUTCDate() + (week - 1) * 7);
    const toExcl = new Date(from); toExcl.setUTCDate(from.getUTCDate() + 7);
    return { from: ymdUTC(from), toExcl: ymdUTC(toExcl) };
  }
  if ((m = /^(\d{4})-(\d{2})$/.exec(k))) {
    const y = Number(m[1]), mo = Number(m[2]);
    return { from: ymdUTC(new Date(Date.UTC(y, mo - 1, 1))), toExcl: ymdUTC(new Date(Date.UTC(y, mo, 1))) };
  }
  if ((m = /^(\d{4})-Q([1-4])$/.exec(k))) {
    const y = Number(m[1]), q = Number(m[2]);
    return { from: ymdUTC(new Date(Date.UTC(y, (q - 1) * 3, 1))), toExcl: ymdUTC(new Date(Date.UTC(y, q * 3, 1))) };
  }
  if ((m = /^(\d{4})$/.exec(k))) {
    const y = Number(m[1]);
    return { from: y + '-01-01', toExcl: (y + 1) + '-01-01' };
  }
  return null;
}

// Every period of `freq` that has FULLY CLOSED in [fromDate, asOfDate] — i.e.
// its toExcl <= asOfDate. Drives both the nightly sweep (recent window) and
// the backfill (fromDate = 2026-01-01).
export function closedPeriods(freq, fromDate, asOfDate) {
  const keys = [];
  let cursor = fromDate;
  const keyFn = { week: weekKey, month: monthKey, quarter: quarterKey, year: yearKey }[freq];
  if (!keyFn) return keys;
  let guard = 0;
  while (cursor < asOfDate && guard++ < 4000) {
    const k = keyFn(cursor);
    const r = periodRange(k);
    if (r && r.toExcl <= asOfDate && keys.indexOf(k) === -1) keys.push(k);
    cursor = r ? r.toExcl : addDays(cursor, 7);
  }
  return keys;
}

// ── The catalogue ─────────────────────────────────────────────────────────────
// scope: 'store' | 'staff'. freqs: which period kinds the cron computes.
// competitive: false = every qualifier gets it (perfect-week, outstanding-review…).
export const BADGES = {
  'demo-champions':       { name: 'Demo Champions',       emoji: '🎁', scope: 'store', freqs: ['week', 'month', 'quarter', 'year'], competitive: true,  metric: 'best demo units as a % of the store\'s own demo target' },
  'addon-masters':        { name: 'Add-on Masters',       emoji: '🧲', scope: 'store', freqs: ['week', 'month', 'quarter', 'year'], competitive: true,  metric: 'best add-on units as a % of the store\'s own add-on target' },
  'customer-favourites':  { name: 'Customer Favourites',  emoji: '⭐', scope: 'store', freqs: ['month', 'quarter', 'year'],          competitive: true,  metric: 'average customer rating (ties: review count)' },
  'svr-stars':            { name: 'SVR Stars',            emoji: '🕵️', scope: 'store', freqs: ['quarter', 'year'],                   competitive: true,  metric: '% of store visits rated green' },
  'learning-leaders':     { name: 'Learning Leaders',     emoji: '🎓', scope: 'store', freqs: ['month', 'quarter'],                  competitive: true,  metric: '% of team fully trained (all 6 modules)' },
  'compliance-champions': { name: 'Compliance Champions', emoji: '✅', scope: 'store', freqs: ['week', 'month', 'quarter', 'year'], competitive: true,  metric: 'fewest compliance issues' },
  'data-capture-kings':   { name: 'Data Capture Kings',   emoji: '📇', scope: 'store', freqs: ['week', 'month'],                     competitive: true,  metric: 'customer email + mobile capture rate' },
  'most-improved':        { name: 'Most Improved',        emoji: '📈', scope: 'store', freqs: ['month'],                             competitive: true,  metric: 'biggest month-on-month add-on gain' },
  'perfect-week':         { name: 'Perfect Week',         emoji: '🗓️', scope: 'store', freqs: ['week'],                              competitive: false, metric: 'zero compliance issues all week' },
  'right-on-time':        { name: 'Right On Time',        emoji: '⏰', scope: 'staff', freqs: ['month'],                             competitive: false, metric: 'zero lates this month (8+ shifts)' },
  'right-on-time-90':     { name: 'Right On Time — 90 days',  emoji: '🥈', scope: 'staff', freqs: ['month'],                         competitive: false, metric: 'zero lates for 90 days (24+ shifts)' },
  'right-on-time-365':    { name: 'Right On Time — a full year', emoji: '🥇', scope: 'staff', freqs: ['month'],                      competitive: false, metric: 'zero lates for 365 days (100+ shifts)' },
  'module-passed':        { name: 'Module Passed',        emoji: '📚', scope: 'staff', freqs: ['ongoing'],                           competitive: false, metric: 'a training module signed off' },
  'fully-trained':        { name: 'Fully Trained',        emoji: '🏅', scope: 'staff', freqs: ['ongoing'],                           competitive: false, metric: 'all 6 modules complete' },
  'outstanding-review':   { name: 'Outstanding Review',   emoji: '🌟', scope: 'staff', freqs: ['quarter'],                           competitive: false, metric: 'performance review 4.5+ out of 5' },
  'induction-star':       { name: 'Induction Star',       emoji: '🚀', scope: 'staff', freqs: ['ongoing'],                           competitive: false, metric: 'all 6 modules inside 6 months' },
  // v132.2 — weekly training (docs/training-plan.md). Weekly Learner is
  // computed by trainingAwards below every sweep; Study Hall's monthly compute
  // lands with the compliance report (PR 4) — the catalogue entry exists now so
  // the badge is one place, as the rule says.
  'weekly-learner':       { name: 'Weekly Learner',       emoji: '📖', scope: 'staff', freqs: ['ongoing'],                           competitive: false, metric: 'four weekly training sessions passed in a row' },
  'study-hall':           { name: 'Study Hall',           emoji: '🏫', scope: 'store', freqs: ['month'],                             competitive: true,  metric: 'highest weekly-training completion %' },
  'customer-heart':       { name: 'Customer Service Heart', emoji: '💙', scope: 'staff', freqs: ['week'],                             competitive: false, metric: 'named by a customer in praise this week (auto weekly; AM/HO can also award by hand)' },
  'star':                 { name: 'Star Badge',           emoji: '⭐', scope: 'any',   freqs: ['adhoc'],                             competitive: false, metric: 'awarded by your Area Manager or Head Office' },
};

// ── Customer Service Heart (💙): a staffer NAMED in a customer's praise ────────
// The customer-feedback AI already extracts praise call-outs carrying the store
// and a staff NAME (or null — it never invents one). This matches a name to ONE
// person in THAT store. Only a CONFIDENT single match awards: 0 matches (unknown)
// or 2+ (ambiguous) are skipped, never guessed. Auto-awarded weekly; AM/HO can
// also award one by hand for a customer's typo or a miss.
//   callouts:  [{ kind, store, staff, quote? }]  (from the feedback AI)
//   staffRows: [{ id, name, store, active }]
// Returns [{ staff_id, store, quote }] — one heart per person (first praise wins).
function heartNormName(s) { return String(s || '').toLowerCase().replace(/[^a-z ]/g, ' ').replace(/\s+/g, ' ').trim(); }
export function matchPraiseToStaff(callouts, staffRows) {
  const byStore = {};
  (staffRows || []).forEach((s) => {
    if (!s || s.active === false || !s.store) return;
    (byStore[s.store] || (byStore[s.store] = [])).push(s);
  });
  const out = {};
  (callouts || []).forEach((c) => {
    if (!c || c.kind !== 'praise' || !c.staff || !c.store) return;
    const want = heartNormName(c.staff);
    if (!want) return;
    const hits = (byStore[c.store] || []).filter((s) => {
      const full = heartNormName(s.name);
      if (!full) return false;
      const tokens = full.split(' ');
      // a customer usually gives a first name; also accept the full name or a
      // name token (a middle name), but require ONE staffer to match for confidence.
      return full === want || tokens[0] === want || tokens.indexOf(want) !== -1;
    });
    if (hits.length !== 1) return;   // 0 = no match, 2+ = ambiguous → skip
    const id = hits[0].id;
    if (!(id in out)) out[id] = { staff_id: id, store: c.store, quote: String(c.quote || '').slice(0, 300) };
  });
  return Object.values(out);
}

// ── Winner picking (competitive badges) ──────────────────────────────────────
// entries: [{ key, value, count? }] (key = store name or staff id).
// opts: { floor?: (e)=>bool, higherIsBetter?: bool (default true),
//         tieBreakCount?: bool (customer-favourites) }
// Returns the WINNERS array (joint winners allowed), [] when nobody qualifies.
export function pickWinners(entries, opts) {
  opts = opts || {};
  const higher = opts.higherIsBetter !== false;
  let pool = (entries || []).filter((e) => e && e.value != null && isFinite(Number(e.value)));
  if (opts.floor) pool = pool.filter(opts.floor);
  if (!pool.length) return [];
  const best = pool.reduce((acc, e) => {
    const v = Number(e.value);
    if (acc == null) return v;
    return higher ? Math.max(acc, v) : Math.min(acc, v);
  }, null);
  let winners = pool.filter((e) => Number(e.value) === best);
  if (winners.length > 1 && opts.tieBreakCount) {
    const maxCount = Math.max(...winners.map((e) => Number(e.count) || 0));
    winners = winners.filter((e) => (Number(e.count) || 0) === maxCount);
  }
  return winners;
}

// Podium detail for the announcement (top 3 INCLUDING the winner) — the one
// place runners-up appear, framed as a podium, never as losers.
export function podium(entries, opts) {
  opts = opts || {};
  const higher = opts.higherIsBetter !== false;
  let pool = (entries || []).filter((e) => e && e.value != null && isFinite(Number(e.value)));
  if (opts.floor) pool = pool.filter(opts.floor);
  pool.sort((a, b) => (higher ? Number(b.value) - Number(a.value) : Number(a.value) - Number(b.value)) || (Number(b.count) || 0) - (Number(a.count) || 0));
  return pool.slice(0, 3).map((e) => ({ key: e.key, value: Math.round(Number(e.value) * 100) / 100, count: e.count }));
}

// ── Metric builders (rows in → entries out; all pure) ────────────────────────
const r2 = (n) => Math.round(n * 100) / 100;

// Add-on style: units per 1,000 AED of sales. salesByStore: {store: totalAED}.
export function unitsPerThousand(unitRows, salesByStore, fields) {
  const units = {};
  (unitRows || []).forEach((row) => {
    if (!row || !row.store) return;
    let u = 0;
    fields.forEach((f) => { u += Number(row[f]) || 0; });
    units[row.store] = (units[row.store] || 0) + u;
  });
  return Object.keys(units).map((store) => {
    const sales = Number(salesByStore && salesByStore[store]) || 0;
    if (sales <= 0 || units[store] <= 0) return { key: store, value: null };
    return { key: store, value: r2(units[store] / (sales / 1000)), count: units[store] };
  });
}

export function demoEntries(addonRows, salesByStore)  { return unitsPerThousand(addonRows, salesByStore, ['demo_units']); }
export function addonEntries(addonRows, salesByStore) { return unitsPerThousand(addonRows, salesByStore, ['batteries', 'gift_wrap', 'reusable_bag', 'gift_cards']); }

// v126.40 (Matt: demo stars "should be best % vs their demo target which is
// reported", not units-per-1,000-AED). The winner is the store that beat its OWN
// target hardest — inherently size-fair, and the target is the SAME number the
// KPI Day tab shows. kpiRows come from the KPI engine (handleKpiComparison), each
// carrying actual_* + target_* per add-on category for the period. value = Σactual
// ÷ Σtarget × 100; a store with no target (0) can't be ranked (value null).
function pctVsTarget(actual, target) { return target > 0 ? r2((actual / target) * 100) : null; }
export function demoTargetEntries(kpiRows) {
  return (kpiRows || []).filter((r) => r && r.store).map((r) => ({
    key: r.store, value: pctVsTarget(Number(r.actual_demo_units) || 0, Number(r.target_demo_units) || 0),
    count: Number(r.actual_demo_units) || 0,
  }));
}
export function addonTargetEntries(kpiRows) {
  const F = ['batteries', 'gift_wrap', 'reusable_bag', 'gift_cards'];
  return (kpiRows || []).filter((r) => r && r.store).map((r) => {
    let a = 0, t = 0;
    F.forEach((f) => { a += Number(r['actual_' + f]) || 0; t += Number(r['target_' + f]) || 0; });
    return { key: r.store, value: pctVsTarget(a, t), count: a };
  });
}

// Customer favourites: avg rating (1–10) + count; floor + tie handled by caller.
export function reviewEntries(feedbackRows) {
  const agg = {};
  (feedbackRows || []).forEach((r) => {
    if (!r || !r.store || r.status === 'deleted' || r.rating == null) return;
    const a = agg[r.store] || (agg[r.store] = { sum: 0, n: 0 });
    a.sum += Number(r.rating) || 0; a.n++;
  });
  return Object.keys(agg).map((store) => ({ key: store, value: r2(agg[store].sum / agg[store].n), count: agg[store].n }));
}

// SVR stars: % green of submitted/reviewed visits.
export function svrEntries(visitRows) {
  const agg = {};
  (visitRows || []).forEach((v) => {
    if (!v || !v.store) return;
    if (v.status !== 'submitted' && v.status !== 'reviewed') return;
    const a = agg[v.store] || (agg[v.store] = { green: 0, n: 0 });
    if (v.overall_rag === 'green') a.green++;
    a.n++;
  });
  return Object.keys(agg).map((store) => ({ key: store, value: r2((agg[store].green / agg[store].n) * 100), count: agg[store].n }));
}

// Learning leaders: % of the store's induction people with all 6 modules dated.
const MODULE_FIELDS = ['m1_date', 'm2_date', 'm3_date', 'm4_date', 'm5_date', 'm6_date'];
export function fullyTrainedRow(row) { return MODULE_FIELDS.every((f) => row && row[f]); }
// ── Individual ongoing badge from weekly training (v132.2) ──────────────────
// Weekly Learner: FOUR published sessions passed in a row, by week_start.
// period = the week_start of the fourth week, so a run of eight earns it twice
// at distinct periods and a re-run can never double-award the same one. Only
// PUBLISHED sessions count as weeks (a declined or skipped week is not a gap
// the person could have filled), and only a completion with passed_at counts.
export const WEEKLY_LEARNER_STREAK = 4;
export function trainingAwards(sessions, completions) {
  const weeks = (sessions || [])
    .filter((s) => s && s.status === 'published' && s.week_start && s.id != null)
    .map((s) => ({ id: String(s.id), ws: String(s.week_start).slice(0, 10) }))
    .sort((a, b) => (a.ws < b.ws ? -1 : a.ws > b.ws ? 1 : 0));
  if (!weeks.length) return [];
  const passedBy = {};
  (completions || []).forEach((c) => {
    if (!c || !c.passed_at || c.staff_id == null || c.session_id == null) return;
    (passedBy[c.staff_id] = passedBy[c.staff_id] || new Set()).add(String(c.session_id));
  });
  const out = [];
  Object.keys(passedBy).forEach((sid) => {
    let run = 0;
    weeks.forEach((w) => {
      if (!passedBy[sid].has(w.id)) { run = 0; return; }
      run++;
      if (run % WEEKLY_LEARNER_STREAK === 0) out.push({ badgeKey: 'weekly-learner', period: 'streak:' + w.ws, staffId: Number(sid), value: run });
    });
  });
  return out;
}

// ── Study Hall (store, monthly): weekly-training completion % ───────────────
// sessions    : training_sessions rows (id, week_start, status)
// completions : training_completions rows with passed_at (staff_id, session_id, store)
// staffRows   : active staff (id, store) — the denominator per store
// from/toExcl : the period window, applied to week_start
// value = passed person-weeks / (staff in store x published weeks in window).
// The completion's store SNAPSHOT wins over the person's current store, so a
// transfer mid-month credits the store they were in that week (v131.84).
// Caller filters count (staff in store) like learning-leaders does.
export function studyHallEntries(sessions, completions, staffRows, from, toExcl) {
  const weeks = (sessions || []).filter((s) => s && s.status === 'published' && s.week_start
    && String(s.week_start).slice(0, 10) >= String(from) && String(s.week_start).slice(0, 10) < String(toExcl));
  if (!weeks.length) return [];
  const weekIds = new Set(weeks.map((s) => String(s.id)));
  const staffStore = {}; const perStore = {};
  (staffRows || []).forEach((s) => {
    if (!s || s.id == null || !s.store) return;
    staffStore[String(s.id)] = s.store;
    perStore[s.store] = perStore[s.store] || { staff: 0, passed: 0 };
    perStore[s.store].staff++;
  });
  (completions || []).forEach((c) => {
    if (!c || !c.passed_at || !weekIds.has(String(c.session_id))) return;
    const store = c.store || staffStore[String(c.staff_id)];
    if (!store || !perStore[store]) return;
    perStore[store].passed++;
  });
  return Object.keys(perStore).map((store) => {
    const p = perStore[store];
    const denom = p.staff * weeks.length;
    return { key: store, value: denom ? r2((p.passed / denom) * 100) : 0, count: p.staff };
  });
}

export function learningEntries(inductionRows) {
  const agg = {};
  (inductionRows || []).forEach((row) => {
    if (!row || !row.store) return;
    const a = agg[row.store] || (agg[row.store] = { done: 0, n: 0 });
    if (fullyTrainedRow(row)) a.done++;
    a.n++;
  });
  return Object.keys(agg).map((store) => ({ key: store, value: r2((agg[store].done / agg[store].n) * 100), count: agg[store].n }));
}

// Compliance: fewest issues wins (issues already computed per store by the
// existing compliance summary — we just count). issueRows: [{store}].
export function complianceEntries(issueCounts, allStores) {
  // every ACTIVE store enters with its count (a store with zero issues must
  // appear — zero is the whole point).
  return (allStores || []).map((store) => ({ key: store, value: Number((issueCounts && issueCounts[store]) || 0) }));
}

// Data capture: (mobiles + emails) / (2 × bills) %, the same roll-up rule the
// KPI tab uses (sum then divide).
export function captureEntries(captureRows) {
  const agg = {};
  (captureRows || []).forEach((r) => {
    if (!r || !r.store) return;
    const a = agg[r.store] || (agg[r.store] = { bills: 0, got: 0 });
    a.bills += Number(r.bills) || 0;
    a.got += (Number(r.mobiles) || 0) + (Number(r.emails) || 0);
  });
  return Object.keys(agg).map((store) => {
    const a = agg[store];
    if (a.bills <= 0) return { key: store, value: null };
    return { key: store, value: r2((a.got / (2 * a.bills)) * 100), count: a.bills };
  });
}

// Most improved: current vs previous combined add-on rate (incl demo) — the
// biggest POSITIVE gain wins; stores must have sales in BOTH months.
export function improvementEntries(curRows, curSales, prevRows, prevSales) {
  const cur = {}, prev = {};
  unitsPerThousand(curRows, curSales, ['demo_units', 'batteries', 'gift_wrap', 'reusable_bag', 'gift_cards'])
    .forEach((e) => { if (e.value != null) cur[e.key] = e.value; });
  unitsPerThousand(prevRows, prevSales, ['demo_units', 'batteries', 'gift_wrap', 'reusable_bag', 'gift_cards'])
    .forEach((e) => { if (e.value != null) prev[e.key] = e.value; });
  return Object.keys(cur)
    .filter((store) => prev[store] != null)
    .map((store) => ({ key: store, value: r2(cur[store] - prev[store]) }))
    .filter((e) => e.value > 0);
}

// ── Lates (Right On Time) ────────────────────────────────────────────────────
// shifts: [{staff_id, day, start_time}] (rota'd WORK shifts only — caller
// filters types). clocks: [{staff_id, date, clock_in}]. Grace: 5 minutes.
// A no-show is NOT a late (absence has its own processes); late = clocked in
// 5+ minutes after the rota start on a day they worked.
export const LATE_GRACE_MIN = 5;
export function computeLates(shifts, clocks) {
  const clockByKey = {};
  (clocks || []).forEach((c) => {
    if (c && c.staff_id != null && c.date && c.clock_in) clockByKey[c.staff_id + '|' + c.date] = c.clock_in;
  });
  const per = {};
  (shifts || []).forEach((s) => {
    if (!s || s.staff_id == null || !s.day || !s.start_time) return;
    const ci = clockByKey[s.staff_id + '|' + s.day];
    if (!ci) return;                                   // no clock row → not counted
    const p = per[s.staff_id] || (per[s.staff_id] = { clockedShifts: 0, lates: 0 });
    p.clockedShifts++;
    // clock_in ISO 'YYYY-MM-DDTHH:MM[:SS]'; shift start 'HH:MM[:SS]'.
    const t = String(ci).slice(11, 16);
    const start = String(s.start_time).slice(0, 5);
    if (t && start) {
      const mins = (Number(t.slice(0, 2)) * 60 + Number(t.slice(3, 5))) - (Number(start.slice(0, 2)) * 60 + Number(start.slice(3, 5)));
      if (mins > LATE_GRACE_MIN) p.lates++;
    }
  });
  return per;
}
// The three tiers off one lates map: entries of staff ids that QUALIFY.
export function onTimeQualifiers(latesByStaff, minShifts) {
  return Object.keys(latesByStaff || {})
    .filter((id) => latesByStaff[id].lates === 0 && latesByStaff[id].clockedShifts >= minShifts)
    .map((id) => ({ key: Number(id), value: latesByStaff[id].clockedShifts }));
}

// ── Individual ongoing badges from induction rows ────────────────────────────
// Awards are (badge_key, period) unique per staff — period encodes the module
// + cycle so re-runs and backfills can never double-award.
export function moduleAwards(inductionRows, staffIdByRef) {
  const out = [];
  (inductionRows || []).forEach((row) => {
    const sid = row && (staffIdByRef[row.staff_ref] != null ? staffIdByRef[row.staff_ref] : null);
    if (sid == null) return;
    const cycle = row.cycle != null ? String(row.cycle) : '1';
    MODULE_FIELDS.forEach((f, i) => {
      if (row[f]) out.push({ badgeKey: 'module-passed', period: 'm' + (i + 1) + ':' + cycle, staffId: sid, value: i + 1 });
    });
    if (fullyTrainedRow(row)) {
      out.push({ badgeKey: 'fully-trained', period: 'cycle:' + cycle, staffId: sid, value: 6 });
      // Induction Star: all 6 inside 183 days, first module to last.
      const times = MODULE_FIELDS.map((f) => Date.parse(row[f])).filter((t) => !isNaN(t));
      if (times.length === 6) {
        const spanDays = (Math.max(...times) - Math.min(...times)) / 86400000;
        if (spanDays <= 183) out.push({ badgeKey: 'induction-star', period: 'cycle:' + cycle, staffId: sid, value: Math.round(spanDays) });
      }
    }
  });
  return out;
}

// Outstanding review: final quarterly reviews with overall >= 4.5.
export function outstandingReviewAwards(reviewRows, staffIdByRef) {
  const out = [];
  (reviewRows || []).forEach((r) => {
    if (!r || r.status !== 'final' || r.overall == null || Number(r.overall) < 4.5) return;
    const sid = staffIdByRef[r.staff_ref];
    if (sid == null || !r.quarter) return;
    out.push({ badgeKey: 'outstanding-review', period: String(r.quarter), staffId: sid, value: r2(Number(r.overall)) });
  });
  return out;
}
